/**
 * FAQ Manager - A module for handling FAQ functionality
 */
class FAQManager {
  constructor() {
    // Core elements
    this.container = document.querySelector('.faq-container');
    this.desktopCategoryLinks = document.querySelectorAll('.faq-sidebar .faq-index-item__link');
    this.mobileCategoryLinks = document.querySelectorAll('.faq-mobile-categories .faq-index-item__link');
    this.allCategoryLinks = document.querySelectorAll('.faq-index-item__link');
    this.faqCategories = document.querySelectorAll('.faq-category');
    this.searchInput = document.querySelector('.faq-search__input');
    
    // Settings
    this.initialCategoryId = null;
    this.highlightClass = 'faq-highlight';
    this.mediaQuery = window.matchMedia('(max-width: 991px)');
    
    // Initial setup
    this.addHighlightStyles();
    this.storeInitialCategory();
  }

  /**
   * Initialize all FAQ functionality
   */
  init() {
    this.sortFAQsByPosition();
    this.processYouTubeVideos();
    this.setupCategoryNavigation();
    this.setupSearchFunctionality();
    this.setupResponsiveHandling();
    this.handleUrlHash();
  }

  /**
   * Add CSS styles for highlighted text
   */
  addHighlightStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .${this.highlightClass} {
        background-color: #FFEB3B;
        color: #000;
        padding: 0 2px;
        border-radius: 2px;
        font-weight: bold;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Store the initial category ID for reset functionality
   */
  storeInitialCategory() {
    if (this.allCategoryLinks.length > 0) {
      const activeLink = document.querySelector('.faq-index-item__link.active');
      if (activeLink) {
        this.initialCategoryId = activeLink.dataset.category;
      } else {
        this.initialCategoryId = this.allCategoryLinks[0].dataset.category;
        
        // Activate the first link in both desktop and mobile views
        const desktopFirstLink = this.desktopCategoryLinks[0];
        const mobileFirstLink = this.mobileCategoryLinks[0];
        
        if (desktopFirstLink) desktopFirstLink.classList.add('active');
        if (mobileFirstLink) mobileFirstLink.classList.add('active');
      }
    }
  }

  /**
   * Sort FAQs by position value within each category
   */
  sortFAQsByPosition() {
    this.faqCategories.forEach((category) => {
      const faqItems = Array.from(category.querySelectorAll('.faq-item'));
      const parentElement = faqItems.length > 0 ? faqItems[0].parentNode : null;

      if (parentElement) {
        // Sort blocks by position
        faqItems.sort((a, b) => {
          const posA = parseInt(a.getAttribute('data-position')) || 0;
          const posB = parseInt(b.getAttribute('data-position')) || 0;
          return posA - posB;
        });

        // Remove all blocks from parent and add them back in sorted order
        faqItems.forEach((item) => item.remove());
        faqItems.forEach((item) => parentElement.appendChild(item));
      }
    });
  }

  /**
   * Process YouTube links and create embeds
   */
  processYouTubeVideos() {
    document.querySelectorAll('.youtube-video-container').forEach((container) => {
      const rawUrl = container.getAttribute('data-raw-url');
      if (!rawUrl) return;

      const videoId = this.extractYouTubeId(rawUrl);
      if (!videoId) {
        // If video ID couldn't be extracted, just show the link
        container.innerHTML = `<a href="${rawUrl}" target="_blank" rel="noopener">${rawUrl}</a>`;
        return;
      }

      // Create the embed container with iframe
      container.appendChild(this.createYouTubeEmbed(videoId));
    });
  }

  /**
   * Create a YouTube embed container with iframe
   * @param {string} videoId - The YouTube video ID
   * @returns {HTMLElement} - The embed container with iframe
   */
  createYouTubeEmbed(videoId) {
    const embedContainer = document.createElement('div');
    embedContainer.className = 'youtube-embed';

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${videoId}`;
    iframe.title = 'YouTube video player';
    iframe.frameBorder = '0';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;

    embedContainer.appendChild(iframe);
    return embedContainer;
  }

  /**
   * Extract YouTube video ID from various URL formats
   * @param {string} url - The YouTube URL
   * @returns {string|null} - The extracted video ID or null
   */
  extractYouTubeId(url) {
    if (!url) return null;

    // Clean up the URL
    const cleanUrl = url.trim();

    // Extract ID from youtube.com/watch?v= format
    let match = cleanUrl.match(
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/i
    );
    if (match && match[1]) return match[1];

    // Extract ID from youtu.be/ format
    match = cleanUrl.match(/(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?&]+)/i);
    if (match && match[1]) return match[1];

    // Extract ID from youtube.com/embed/ format
    match = cleanUrl.match(
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?&]+)/i
    );
    if (match && match[1]) return match[1];

    // Extract ID directly if it's just the ID (11 characters)
    if (/^[a-zA-Z0-9_-]{11}$/.test(cleanUrl)) return cleanUrl;

    return null;
  }

  /**
   * Setup category navigation
   */
  setupCategoryNavigation() {
    // Setup desktop category links
    this.desktopCategoryLinks.forEach((link) => {
      link.addEventListener('click', this.handleCategoryClick.bind(this));
    });
    
    // Setup mobile category links
    this.mobileCategoryLinks.forEach((link) => {
      link.addEventListener('click', this.handleCategoryClick.bind(this));
    });
  }

  /**
   * Handle category link click
   * @param {Event} e - The click event
   */
  handleCategoryClick(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const categoryId = link.dataset.category;

    this.navigateToCategory(categoryId);
    
    // If we're on mobile, scroll to the category content
    if (this.mediaQuery.matches) {
      const selectedCategory = document.querySelector(`.faq-category[data-category="${categoryId}"]`);
      if (selectedCategory) {
        setTimeout(() => {
          selectedCategory.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }
  
  /**
   * Navigate to specified category
   * @param {string} categoryId - The category ID to navigate to
   */
  navigateToCategory(categoryId) {
    // Remove active class from all links (both desktop and mobile)
    this.allCategoryLinks.forEach((link) => {
      link.classList.remove('active');
    });

    // Add active class to matching links in both desktop and mobile views
    const desktopActiveLink = document.querySelector(`.faq-sidebar .faq-index-item__link[data-category="${categoryId}"]`);
    const mobileActiveLink = document.querySelector(`.faq-mobile-categories .faq-index-item__link[data-category="${categoryId}"]`);
    
    if (desktopActiveLink) {
      desktopActiveLink.classList.add('active');
      desktopActiveLink.setAttribute('aria-selected', 'true');
    }
    
    if (mobileActiveLink) {
      mobileActiveLink.classList.add('active');
      mobileActiveLink.setAttribute('aria-selected', 'true');
    }

    // Hide all FAQ categories
    this.faqCategories.forEach((category) => {
      category.style.display = 'none';
    });

    // Show the selected category
    const selectedCategory = document.querySelector(
      `.faq-category[data-category="${categoryId}"]`
    );
    if (selectedCategory) {
      selectedCategory.style.display = 'block';
    }
    
    // Update URL hash for bookmarking
    window.history.replaceState(null, null, `#faq-${categoryId}`);
  }

  /**
   * Setup search functionality
   */
  setupSearchFunctionality() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener(
      'input',
      this.handleSearchInput.bind(this)
    );
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.searchInput.value = '';
        this.resetToInitialCategory();
      }
    });
    this.searchInput.addEventListener('keyup', (e) => {
      if (this.searchInput.value === '') {
        this.resetToInitialCategory();
      }
    });
  }

  /**
   * Handle search input
   */
  handleSearchInput() {
    const searchTerm = this.searchInput.value.toLowerCase().trim();

    if (searchTerm === '') {
      this.resetToInitialCategory();
      return;
    }

    // Show all FAQ categories for searching
    this.faqCategories.forEach((category) => {
      category.style.display = 'block';
    });

    // Make all category links inactive during search
    this.allCategoryLinks.forEach((link) => {
      link.classList.remove('active');
      link.setAttribute('aria-selected', 'false');
    });

    // Get all FAQ items
    const faqItems = document.querySelectorAll('.faq-item');
    let hasVisibleItems = false;

    // Reset any previous highlighting
    this.removeAllHighlights();

    // Filter FAQ items based on search term
    faqItems.forEach((item) => {
      const questionElement = item.querySelector('.faq-question');
      const question = questionElement.textContent.toLowerCase();
      const answerElement = item.querySelector('.faq-answer__content');
      const answer = answerElement
        ? answerElement.textContent.toLowerCase()
        : '';

      if (question.includes(searchTerm) || answer.includes(searchTerm)) {
        item.style.display = 'block';
        hasVisibleItems = true;

        // Highlight matching text
        this.highlightText(questionElement, searchTerm);
        if (answerElement) {
          this.highlightText(answerElement, searchTerm);
        }

        // Auto-expand the FAQ item to show the highlighted content
        const details = item.querySelector('details');
        if (details && !details.open) {
          details.open = true;
        }
      } else {
        item.style.display = 'none';
      }
    });

    // Handle no results case
    this.handleNoSearchResults(hasVisibleItems);
  }

  /**
   * Safely highlight text in HTML content
   * @param {HTMLElement} element - The element containing HTML to highlight text in
   * @param {string} searchTerm - The search term to highlight
   */
  highlightText(element, searchTerm) {
    // Safety check
    if (!element || !searchTerm || searchTerm.length < 2) return;

    // Function to safely highlight text within text nodes only
    const highlightTextInNode = (textNode, term) => {
      const text = textNode.nodeValue;
      const regex = new RegExp(this.escapeRegExp(term), 'gi');

      if (!regex.test(text)) return false;

      // Split the text by the search term
      const parts = text.split(
        new RegExp(`(${this.escapeRegExp(term)})`, 'gi')
      );

      // Create a document fragment to hold the new nodes
      const fragment = document.createDocumentFragment();

      // Process each part
      parts.forEach((part) => {
        if (part.toLowerCase() === term.toLowerCase()) {
          // This part matches the search term - highlight it
          const span = document.createElement('span');
          span.className = this.highlightClass;
          span.textContent = part;
          fragment.appendChild(span);
        } else if (part) {
          // This is just regular text
          fragment.appendChild(document.createTextNode(part));
        }
      });

      // Replace the original text node with the fragment
      textNode.parentNode.replaceChild(fragment, textNode);
      return true;
    };

    // Function to recursively walk through all nodes in the element
    const walkNodeTree = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Text node - check if it contains the search term
        return highlightTextInNode(node, searchTerm);
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.nodeName !== 'SCRIPT' &&
        node.nodeName !== 'STYLE' &&
        !node.classList.contains(this.highlightClass)
      ) {
        // Element node - process its children, but skip certain elements
        let hasHighlight = false;
        // Create a copy of childNodes to avoid live collection issues during modification
        const childNodes = Array.from(node.childNodes);
        childNodes.forEach((child) => {
          if (walkNodeTree(child)) {
            hasHighlight = true;
          }
        });
        return hasHighlight;
      }
      return false;
    };

    // Process the element's content
    walkNodeTree(element);
  }

  /**
   * Remove all highlight spans from the FAQ content
   */
  removeAllHighlights() {
    const highlights = document.querySelectorAll(`.${this.highlightClass}`);

    highlights.forEach((highlight) => {
      // Replace the highlight span with just its text content
      const textNode = document.createTextNode(highlight.textContent);
      highlight.parentNode.replaceChild(textNode, highlight);
    });

    // Also normalize any text nodes that got split during highlighting
    document
      .querySelectorAll('.faq-question, .faq-answer__content')
      .forEach((element) => {
        element.normalize();
      });
  }

  /**
   * Escape special characters for use in regex
   * @param {string} string - String to escape
   * @returns {string} - Escaped string safe for regex
   */
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Handle no search results message
   * @param {boolean} hasVisibleItems - Whether there are visible items
   */
  handleNoSearchResults(hasVisibleItems) {
    // Remove any existing no results messages
    document
      .querySelectorAll('.no-results-message')
      .forEach((msg) => msg.remove());

    if (!hasVisibleItems) {
      this.faqCategories.forEach((container) => {
        if (!container.querySelector('.no-results-message')) {
          const noResultsMessage = document.createElement('div');
          noResultsMessage.className = 'no-results-message';
          noResultsMessage.textContent = 'No FAQs found matching your search.';
          container.appendChild(noResultsMessage);
        }
      });
    }
  }

  /**
   * Reset to the initial or active category
   */
  resetToInitialCategory() {
    // Remove any "no results" messages
    document
      .querySelectorAll('.no-results-message')
      .forEach((msg) => msg.remove());

    // Remove all highlights
    this.removeAllHighlights();

    // Reset display of all FAQ items
    document.querySelectorAll('.faq-item').forEach((item) => {
      item.style.display = 'block';
    });

    // Hide all FAQ categories
    this.faqCategories.forEach((category) => {
      category.style.display = 'none';
    });

    // Find the active link or use the first one
    const activeLink = document.querySelector('.faq-index-item__link.active');
    let categoryId;

    if (activeLink) {
      categoryId = activeLink.dataset.category;
    } else if (this.initialCategoryId) {
      categoryId = this.initialCategoryId;

      // If no active link, activate the initial one in both desktop and mobile
      const desktopInitialLink = document.querySelector(
        `.faq-sidebar .faq-index-item__link[data-category="${this.initialCategoryId}"]`
      );
      const mobileInitialLink = document.querySelector(
        `.faq-mobile-categories .faq-index-item__link[data-category="${this.initialCategoryId}"]`
      );
      
      if (desktopInitialLink) {
        desktopInitialLink.classList.add('active');
      }
      
      if (mobileInitialLink) {
        mobileInitialLink.classList.add('active');
      }
    } else if (this.allCategoryLinks.length > 0) {
      // Fallback to first category
      categoryId = this.allCategoryLinks[0].dataset.category;
      
      const desktopFirstLink = this.desktopCategoryLinks[0];
      const mobileFirstLink = this.mobileCategoryLinks[0];
      
      if (desktopFirstLink) desktopFirstLink.classList.add('active');
      if (mobileFirstLink) mobileFirstLink.classList.add('active');
    }

    // Show the selected category
    if (categoryId) {
      const selectedCategory = document.querySelector(
        `.faq-category[data-category="${categoryId}"]`
      );
      if (selectedCategory) {
        selectedCategory.style.display = 'block';
      }
    }

    // Close all FAQ items that were auto-expanded during search
    document.querySelectorAll('details').forEach((details) => {
      details.open = false;
    });
  }

  /**
   * Setup responsive behavior
   */
  setupResponsiveHandling() {
    // Initial check
    this.handleResponsiveChange();
    
    // Listen for breakpoint changes
    this.mediaQuery.addEventListener('change', this.handleResponsiveChange.bind(this));
    
    // Handle mobile category list horizontal scrolling
    if (this.mediaQuery.matches) {
      const mobileCategoryList = document.querySelector('.faq-mobile-categories .faq-category-list');
      if (mobileCategoryList) {
        // Make active category visible by scrolling to it
        const activeLink = mobileCategoryList.querySelector('.faq-index-item__link.active');
        if (activeLink) {
          setTimeout(() => {
            const containerWidth = mobileCategoryList.offsetWidth;
            const activeLinkPosition = activeLink.offsetLeft;
            const scrollPosition = activeLinkPosition - (containerWidth / 2) + (activeLink.offsetWidth / 2);
            mobileCategoryList.scrollLeft = Math.max(0, scrollPosition);
          }, 100);
        }
      }
    }
  }
  
  /**
   * Handle responsive layout changes
   */
  handleResponsiveChange() {
    // No specific actions needed here anymore since we're using CSS to show/hide elements
  }
  
  /**
   * Handle URL hash on page load for direct navigation
   */
  handleUrlHash() {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#faq-')) {
      const categoryId = hash.replace('#faq-', '');
      const categoryExists = document.querySelector(`.faq-index-item__link[data-category="${categoryId}"]`);
      
      if (categoryExists) {
        // Short delay to ensure everything is ready
        setTimeout(() => {
          this.navigateToCategory(categoryId);
        }, 100);
      }
    }
  }
}

// Initialize the FAQ manager when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const faqManager = new FAQManager();
  faqManager.init();
});